export type ApiToken = string;

export type UserRole = "owner" | "operator" | "promoter";

export type User = {
  id: string;
  email: string;
  full_name: string;
  company?: string | null;
  phone?: string | null;
  job_title?: string | null;
  avatar_url?: string | null;
  is_active: boolean;
  is_system_admin?: boolean;
  created_at: string;
  updated_at?: string | null;
  role: UserRole;
  owner_id?: string | null;
};

/** Operator User account managed by an owner (GET/POST /company-operators). */
export type CompanyOperatorAccount = {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
};

export type CompanyOperatorCreate = {
  email: string;
  full_name: string;
  password: string;
};

export type CompanyOperatorCredentialsPatch = {
  email?: string;
  password?: string;
};

/** Promoter User account linked to an event (GET /events/{id}/promoter-accounts). */
export type EventPromoterAssignment = {
  id: string;
  event_id: string;
  promoter_user_id: string;
  promoter_email: string;
  promoter_name: string;
  status: string;
  created_at?: string | null;
};

export type EventPromoterAssignmentCreateResult = {
  assignment: EventPromoterAssignment;
  generated_password?: string | null;
};

export type EventPromoterAccountCreate = {
  email: string;
  full_name?: string;
  password?: string;
};

export type CompanyUserSummary = {
  id: string;
  full_name: string;
  email: string;
  job_title?: string | null;
  avatar_url?: string | null;
};

/** Body for PATCH /users/me */
export type UserProfilePatch = {
  full_name?: string;
  company?: string | null;
  phone?: string | null;
  job_title?: string | null;
};

export type ChangePasswordPayload = {
  current_password: string;
  new_password: string;
};

/** GET/PATCH /users/me/notification-preferences */
export type NotificationCategory = "email_digest" | "event_alerts" | "marketing";
export type NotificationChannel = "email" | "whatsapp";

export type NotificationPreferences = {
  // Legacy flat booleans (= the email switches); prefer `channels`.
  email_digest: boolean;
  event_alerts: boolean;
  marketing: boolean;
  channels: Record<NotificationCategory, Record<NotificationChannel, boolean>>;
  /** WhatsApp is false when the provider is not configured on the server. */
  available_channels: Record<NotificationChannel, boolean>;
  whatsapp: {
    verified: boolean;
    /** Masked; the full number is never returned. */
    number: string | null;
    consented_at: string | null;
    verification_pending: boolean;
  };
  updated_at?: string | null;
};

export type NotificationPreferencesUpdate = {
  channels?: Partial<Record<NotificationCategory, Partial<Record<NotificationChannel, boolean>>>>;
};

export type AuthResponse = {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  user: User;
};

export type TicketTier = {
  id: string;
  tier_name: string;
  price: number;
  quantity: number;
  sold: number;
};

export type EventFinance = {
  production_cost: number;
  marketing_cost: number;
  artist_fee: number;
  projected_revenue?: number;
  break_even_tickets?: number;
  profit_margin?: number;
};

export type Staffing = {
  expected_attendees: number;
  security_required: number;
  volunteers_required: number;
  medics_required: number;
};

export type EventListItem = {
  id: string;
  company_id?: string | null;
  venue_id?: string | null;
  artist_id?: string | null;
  name: string;
  artist?: string | null;
  city: string;
  venue_capacity: number;
  event_date: string;
  start_date?: string | null;
  end_date?: string | null;
  status: string;
  hold_level?: number | null;
  /** Legend text for the hold level, e.g. "Hold 1 · first option on the date". */
  hold_label?: string | null;
  created_at: string;
  /** RSVP headcount (sum of party sizes) when provided by the API. */
  rsvp_count?: number;
  /** Projected RSVP revenue when provided by the API. */
  rsvp_projected_revenue?: number;
  /** True when this event is linked to a zoniq.in ticketing event. */
  ticketing_linked?: boolean;
  ticketing_event_id?: number | null;
};

/** Paginated GET /events/ response (backend EventListPage). */
export type EventListPage = {
  items: EventListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type EventReview = {
  id: string;
  event_id: string;
  total_revenue: number;
  total_cost: number;
  net_profit: number;
  created_at: string;
  updated_at?: string | null;
};

export type EventDetail = EventListItem & {
  ticket_tiers: TicketTier[];
  finances?: EventFinance;
  staffing?: Staffing;
};

export type TicketingTierMapping = {
  ticket_tier_id: string;
  ticketing_category_id: number;
};

export type TicketingLink = {
  id: string;
  event_id: string;
  ticketing_event_id: number;
  status: string;
  linked_by_user_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  tier_links: Array<{
    id: string;
    ticket_tier_id: string;
    ticketing_category_id: number;
  }>;
  event_name?: string | null;
  event_city?: string | null;
  event_date?: string | null;
  company_id?: string | null;
  company_name?: string | null;
};

export type AdminCompanySummary = {
  id: string;
  name: string;
};

export type AdminCompanyEvent = {
  id: string;
  name: string;
  city: string;
  event_date: string;
  status: string;
  ticket_tiers: Array<{
    id: string;
    tier_name: string;
    price: number;
    quantity: number;
    sold: number;
  }>;
  company_id?: string | null;
  company_name?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  venue_name?: string | null;
  venue_capacity?: number | null;
  artist?: string | null;
};

export type TicketingCatalogCategory = {
  id: number;
  name: string;
  price: number;
  total_seats: number;
  available_seats: number;
};

export type TicketingCatalogEvent = {
  id: number;
  title: string;
  slug: string;
  status: string;
  event_date?: string | null;
  total_seats: number;
  available_seats: number;
  seat_categories: TicketingCatalogCategory[];
  organizer_name?: string | null;
  city?: string | null;
  /** Sold by activity + time slot: nothing to map, sales sync as activity tickets. */
  has_activities?: boolean;
};

export type RiskSeverity = "low" | "medium" | "high" | "critical";

export type RiskEntry = {
  risk: string;
  state: string;
  severity: RiskSeverity | string;
};

export type RiskAssessment = {
  overall_level: RiskSeverity | string;
  risks: RiskEntry[];
};

export type ForecastPoint = {
  sell_through?: string;
  tickets_sold?: number;
  revenue?: number;
  profit?: number;
  is_break_even?: boolean;
  scenario?: string;
  value?: number;
};

export type RevenueByTier = {
  tier?: string;
  tier_name?: string;
  revenue: number;
  tickets?: number;
  price?: number;
};

export type DashboardData = {
  event: EventDetail;
  metrics: {
    projected_revenue: number;
    total_cost: number;
    profit: number;
    // Margins are null when revenue is 0 (no meaningful percentage exists).
    // The UI must render these as "—" rather than 0.0%.
    profit_margin: number | null;
    break_even_tickets: number;
    // BE-4: range across plausible tier mixes. Either may be null when
    // event cannot break even at any mix (structural loss).
    break_even_tickets_min?: number | null;
    break_even_tickets_max?: number | null;
    break_even_sell_through_pct?: number | null;
    tickets_sold: number;
    door_entries?: number;
    team_on_gate?: number;
    health_score?: number;
    actual_revenue?: number;
    actual_profit?: number;
    actual_profit_margin?: number | null;
    /** Sprint 14: Overview rail "Budget Summary" card. */
    budget_summary?: BudgetSummary;
  };
  ticket_summary: {
    total_tickets: number;
    tickets_sold: number;
    remaining: number;
    revenue_generated: number;
    sell_through_pct: number;
    tiers: Array<{
      tier_id: string;
      name: string;
      price: number;
      quantity: number;
      sold: number;
      revenue: number;
    }>;
  };
  staffing?: Staffing;
  chart_data: {
    revenue_by_tier: RevenueByTier[];
    forecast: ForecastPoint[];
    forecast_model?: string;
    /**
     * Sprint 14: single source for the Overview "Event Cost" pie and the
     * P&L "Expense Analysis" bars, so the two always agree and both
     * reconcile with metrics.total_cost.
     */
    cost_breakdown?: CostBreakdownRow[];
  };
  review?: EventReview | null;
  risk_assessment?: RiskAssessment | null;
};

export type InvoiceLineItem = {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  /** Per-line GST rate; falls back to the invoice's default rate when unset. */
  tax_rate?: number | null;
};

export type InvoiceRecord = {
  id: string;
  event_id: string;
  /** Null while the invoice is a draft — numbers are minted at finalize. */
  invoice_number?: string | null;
  vendor_name?: string | null;
  vendor_address?: string | null;
  consignee_name?: string | null;
  category?: string | null;
  amount: number;
  tax: number;
  total: number;
  status: string;
  /** Vendor payment due date — powers the Vendor Payment Due/Overdue email triggers. */
  due_date?: string | null;
  notes?: string | null;
  line_items?: InvoiceLineItem[] | null;
  /** Full invoice form; previously localStorage-only. */
  form_data?: Record<string, unknown> | null;
  pdf_url?: string | null;
  sent_at?: string | null;
  sent_to?: string | null;
  finalized_at?: string | null;
  created_at: string;
  updated_at?: string | null;
};

export const INVOICE_DRAFT_STATUS = "draft";

export function isDraftInvoice(invoice: Pick<InvoiceRecord, "status">): boolean {
  return (invoice.status || "").trim().toLowerCase() === INVOICE_DRAFT_STATUS;
}

export type InvoiceSortKey = "date" | "status" | "name" | "amount";

/** Party the invoice is billed to; falls back to vendor for legacy rows. */
export function invoiceDisplayName(invoice: InvoiceRecord): string {
  return invoice.consignee_name?.trim() || invoice.vendor_name?.trim() || "Unnamed";
}

export type InvoiceDraftPayload = {
  event_id?: string;
  vendor_name?: string | null;
  vendor_address?: string | null;
  consignee_name?: string | null;
  category?: string | null;
  tax_rate?: number;
  due_date?: string | null;
  notes?: string | null;
  line_items?: InvoiceLineItem[];
  form_data?: Record<string, unknown> | null;
};

export type BudgetSummary = {
  estimated_total: number;
  actual_total: number;
  variance: number;
  /** Null when nothing is budgeted — the percentage is undefined, not 0. */
  variance_pct: number | null;
};

export type CostBreakdownRow = {
  category: string;
  amount: number;
  source: "finance" | "budget";
  estimated?: number | null;
  actual?: number | null;
};

export type ForecastCurvePoint = {
  sell_through: number;
  tickets_sold: number;
  revenue: number;
  profit: number;
  is_break_even: boolean;
};

export type ForecastCurveResponse = {
  event_id: string;
  total_cost: number;
  forecast_model: string;
  points: ForecastCurvePoint[];
};

export type InvoiceSearchGroup = {
  event_id: string;
  event_name: string;
  invoices: InvoiceRecord[];
};

export type PortfolioYAxisMetric =
  | "revenue"
  | "profit"
  | "expense"
  | "rsvps"
  | "tickets"
  | "tickets_walkin"
  | "tickets_rsvp"
  | "tickets_guest_list"
  | "sell_through"
  | "roi";

export type PortfolioXAxisDimension = "event" | "location" | "artist" | "ticket_tier";

export type PortfolioTimeframe = "week" | "month" | "quarter" | "6months" | "year";

export type PortfolioMetricsBucket = {
  bucket: string;
  /** Human label for the axis, e.g. "14 Sep" or "Sep 2026". */
  label?: string;
  groups: Record<string, Record<string, number>>;
};

export type PortfolioMetricsResponse = {
  period_start?: string | null;
  period_end?: string | null;
  series: PortfolioMetricsBucket[];
  totals: Record<string, Record<string, number>>;
};

export type PortfolioSummaryMetric = {
  value: number | null;
  delta_pct: number | null;
};

export type PortfolioSummaryResponse = {
  revenue: PortfolioSummaryMetric;
  profit: PortfolioSummaryMetric;
  expense: PortfolioSummaryMetric;
  roi: PortfolioSummaryMetric;
  avg_sell_through: PortfolioSummaryMetric;
  pending_invoices: PortfolioSummaryMetric;
  period_start?: string | null;
  period_end?: string | null;
};

export type PortfolioInsightRankItem = {
  name: string;
  value: number;
};

export type PortfolioInsightDimension = {
  best: PortfolioInsightRankItem | null;
  worst: PortfolioInsightRankItem | null;
};

export type PortfolioInsightsResponse = {
  venue: PortfolioInsightDimension;
  market: PortfolioInsightDimension;
  artist: PortfolioInsightDimension;
};

export type UpcomingPayout = {
  invoice_id: string;
  invoice_number: string;
  event_id: string;
  event_name: string;
  amount: number;
  status: string;
  created_at: string;
};

export type PortfolioTransaction = {
  invoice_id: string;
  event_id: string;
  event_name: string;
  date: string;
  type: string;
  party?: string | null;
  amount: number;
  status: string;
};

export type EventBreakEvenSummary = {
  tickets_to_go: number;
  break_even_tickets: number;
  tickets_sold: number;
  sell_through_pct: number | null;
};

export type EventPaceSummary = {
  tickets_to_go: number;
  days_to_event: number;
  tickets_sold_per_day: number | null;
  tickets_required_per_day: number | null;
  on_pace: boolean | null;
};

export type EventFinanceOverview = {
  event_id: string;
  event_name: string;
  venue_capacity: number;
  break_even: EventBreakEvenSummary;
  pace: EventPaceSummary;
  revenue: number;
  profit: number;
  expense: number;
  channel_economics: Record<string, unknown> | null;
};

export type EventUpcomingInvoiceItem = {
  invoice_id: string;
  invoice_number: string;
  amount: number;
  status: string;
};

export type EventAiInsights = {
  mode: "pre_event" | "post_event";
  demand?: string | null;
  sellout_eta_days?: number | null;
  health_score?: number | null;
  roi?: number | null;
  upcoming_invoices?: EventUpcomingInvoiceItem[] | null;
};

export type GenerateInvoicePayload = {
  event_id: string;
  vendor_name?: string;
  vendor_address?: string;
  category?: string;
  tax_rate?: number;
  due_date?: string;
  notes?: string;
  line_items?: InvoiceLineItem[];
  consignee_name?: string;
  form_data?: Record<string, unknown> | null;
};

export type InvoiceCalculationResponse = {
  invoice: InvoiceRecord;
  event_name: string;
  company_name?: string | null;
  summary: {
    subtotal: number;
    tax_rate: number;
    tax: number;
    total: number;
    line_item_count: number;
    currency: string;
  };
};

export type Artist = {
  id: string;
  company_id: string;
  name: string;
  stage_name?: string | null;
  email?: string | null;
  phone?: string | null;
  /** Legacy combined contact (email · phone). */
  contact?: string | null;
  genre?: string | null;
  manager_name?: string | null;
  manager_phone?: string | null;
  notes?: string | null;
  base_cost: number;
  /** Formatted base address summary. */
  current_location?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  landmark?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
  photo_url?: string | null;
  created_at: string;
  updated_at?: string | null;
};

export type ContactRole = "agent" | "manager" | "label" | "co_promoter";

export const CONTACT_ROLES: readonly ContactRole[] = ["agent", "manager", "label", "co_promoter"];

export type ContactArtistSummary = {
  id: string;
  name: string;
};

export type Contact = {
  id: string;
  company_id: string;
  name: string;
  role?: ContactRole | string | null;
  agency?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  photo_url?: string | null;
  artists: ContactArtistSummary[];
  created_at: string;
  updated_at?: string | null;
};

export type Venue = {
  id: string;
  company_id: string;
  venue_name: string;
  /** Formatted full address (legacy + display summary). */
  location: string;
  address_line1?: string | null;
  address_line2?: string | null;
  landmark?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
  capacity?: number | null;
  base_cost?: number | null;
  photo_url?: string | null;
  created_at: string;
  updated_at?: string | null;
};

export type ContactCreate = {
  name: string;
  role?: ContactRole | null;
  agency?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  artist_ids?: string[];
};

export type ContactUpdate = {
  name?: string;
  role?: ContactRole | null;
  agency?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  artist_ids?: string[];
};

export type Company = {
  id: string;
  name: string;
  /** Original filename only — fetch the file itself via companiesApi.logoUrl()/invoiceTemplateUrl(). */
  logo_filename?: string | null;
  invoice_template_filename?: string | null;
  /** Default RSVP/ticket template for new events (Profile picker) */
  default_ticket_template_id?: "black" | "white" | string | null;
  invoice_gstin?: string | null;
  invoice_address?: string | null;
  invoice_state_name?: string | null;
  invoice_state_code?: string | null;
  bank_account_holder?: string | null;
  bank_account_number?: string | null;
  bank_ifsc_code?: string | null;
  bank_branch?: string | null;
  created_at: string;
};

export type Budget = {
  id: string;
  event_id: string;
  category: string;
  estimated_cost: number;
  actual_cost: number;
};

export type Task = {
  id: string;
  event_id: string;
  description: string;
  assignee?: string | null;
  assignee_user_id?: string | null;
  due_date?: string | null;
  priority?: string | null;
  status: string;
};

export type Schedule = {
  id: string;
  event_id: string;
  artist_id?: string | null;
  venue_id?: string | null;
  scheduled_time: string;
};

export type UiStatus = "idle" | "loading" | "ready" | "error";

export type UiMetric = {
  label: string;
  value: string;
  delta?: string;
  tone?: "default" | "success" | "danger" | "warning" | "info";
  progress?: number;
};

export type UiInsight = {
  id: string;
  title: string;
  text: string;
  confidence?: string;
};

export type UiAlert = {
  id: string;
  level: "critical" | "warning" | "info";
  title: string;
  subtitle?: string;
};

export type UiTable = {
  headers: string[];
  rows: string[][];
};

export type UiChartPoint = {
  name: string;
  value: number;
};

export type LiveOpsTier = {
  tier_id: string;
  name: string;
  price: number;
  quantity: number;
  sold: number;
};

export type TicketEntryLog = {
  id: string;
  event_id: string;
  tier_id: string | null;
  tier_name: string | null;
  operator_id: string | null;
  operator_name: string | null;
  operator_email: string | null;
  ticketing_partner_id: string | null;
  ticketing_partner_name: string | null;
  rsvp_guest_id: string | null;
  payment_mode: PaymentMode | null;
  quantity_sold: number;
  entry_cost: number;
  occurred_at: string;
  notes: string | null;
  created_at: string;
};

export type PaymentMode = "cash" | "online";

export type TicketEntryLogCreate = {
  tier_id: string;
  quantity_sold: number;
  entry_cost: number;
  /**
   * Operator attribution for the entry log.
   * If omitted, the backend may infer from auth context.
   */
  operator_id?: string;
  ticketing_partner_id?: string;
  ticketing_partner_name?: string;
  rsvp_guest_id?: string;
  payment_mode?: PaymentMode;
  occurred_at?: string;
  notes?: string;
  /**
   * The number asked for at the gate. A match updates that guest row (Live Ops
   * data wins); no match creates a walk-in row from this entry.
   */
  guest_phone?: string;
  guest_name?: string;
  guest_email?: string;
};

export type TicketEntryLogUpdate = {
  entry_cost?: number;
  payment_mode?: PaymentMode;
  notes?: string;
};

export type TicketingPartner = {
  id: string;
  name: string;
  slug: string;
  is_system: boolean;
};

export type TicketEntryLogListResponse = {
  items: TicketEntryLog[];
  next_cursor: string | null;
  total_count: number;
};

export type TicketEntryLogListParams = {
  tier_id?: string;
  operator_id?: string;
  from?: string;
  to?: string;
  sort_by?: "occurred_at" | "entry_cost" | "created_at";
  direction?: "asc" | "desc";
  cursor?: string;
  limit?: number;
};

export type TeamRole = "operator" | "supervisor" | "admin";

/** A gate team member. The roster is company-wide: the same people work every event. */
export type TeamMember = {
  id: string;
  company_id: string;
  name: string;
  email: string;
  role: TeamRole | string;
  created_at: string;
  updated_at: string | null;
};

export type TeamMemberCreate = {
  name: string;
  email: string;
  role?: TeamRole;
};

export type TeamMemberUpdate = {
  name?: string;
  email?: string;
  role?: TeamRole;
};

export type EventOSData = {
  eventName: string;
  eventSubtitle: string;
  eventHealthScore: number | null;
  overviewMetrics: UiMetric[];
  pnlMetrics: UiMetric[];
  ticketMetrics: UiMetric[];
  venueMetrics: UiMetric[];
  workforceMetrics: UiMetric[];
  vendorMetrics: UiMetric[];
  taskMetrics: UiMetric[];
  riskMetrics: UiMetric[];
  liveMetrics: UiMetric[];
  ticketTiers: LiveOpsTier[];
  scenarioMetrics: UiMetric[];
  reportMetrics: UiMetric[];
  alerts: UiAlert[];
  insights: UiInsight[];
  ticketTable: UiTable;
  workforceTable: UiTable;
  vendorTable: UiTable;
  riskTable: UiTable;
  revenueChart: UiChartPoint[];
  expenseChart: UiChartPoint[];
  ticketTierChart: UiChartPoint[];
  scenarioChart: Array<{
    name: string;
    revenue: number;
    expenses: number;
    netProfit: number;
  }>;
};

export type EventNote = {
  event_id: string;
  body: string;
  updated_at: string;
};

export type DealType = "guarantee" | "versus" | "co_promote" | "door_split";

export type EventDealTerms = {
  /** null is the event-wide row; a uuid scopes the terms to one promoter. */
  promoter_user_id: string | null;
  deal_type: DealType;
  guarantee_inr: number | null;
  artist_split_pct: number | null;
  backend_pct: number | null;
  promoter_share_pct: number | null;
  notes: string | null;
  updated_at: string | null;
};

export type EventDealTermsUpsert = {
  /** omit to write the event-wide row; owners pass a uuid to scope per promoter. */
  promoter_user_id?: string | null;
  deal_type: DealType;
  guarantee_inr?: number | null;
  artist_split_pct?: number | null;
  backend_pct?: number | null;
  promoter_share_pct?: number | null;
  notes?: string | null;
};

export type EventFile = {
  id: string;
  event_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  uploaded_by: string | null;
  created_at: string;
};
